/**
 * SAM Speech Synthesizer Loader
 * Generates speech audio using sam-js (Software Automatic Mouth)
 */

import SamJs from 'sam-js'
import { hashString } from './bytecode.ts'

export interface SamData {
  id: number
  key: string
  audioBuffer: AudioBuffer
}

interface SamParams {
  speak: string
  speed?: number
  pitch?: number
  throat?: number
  mouth?: number
}

export class SamLoader {
  private audioContext: AudioContext
  private loadedSamples = new Map<number, SamData>()

  constructor(audioContext: AudioContext) {
    this.audioContext = audioContext
  }

  async generateSpeech(params: SamParams): Promise<SamData> {
    // Create a unique key for this combination of parameters
    const key = JSON.stringify(params)
    const id = hashString(key)

    // Return cached sample if already generated
    if (this.loadedSamples.has(id)) {
      return this.loadedSamples.get(id)!
    }

    // Normalize 0..1 parameters to 0..255 range (inverted: 0->255, 1->0)
    const speed = Math.round((1 - (params.speed ?? 0.5)) * 255)
    const pitch = Math.round((1 - (params.pitch ?? 0.5)) * 255)
    const throat = Math.round((1 - (params.throat ?? 0.5)) * 255)
    const mouth = Math.round((1 - (params.mouth ?? 0.5)) * 255)

    // Create sam-js instance with parameters
    const sam = new SamJs({
      debug: false,
      speed,
      pitch,
      throat,
      mouth,
    })

    // Generate speech buffer (returns Uint8Array of 8-bit PCM samples or false on error)
    const buffer = sam.buf8(params.speak)

    if (!buffer || typeof buffer === 'boolean' || (buffer as Uint8Array).length === 0) {
      throw new Error(`Failed to generate speech for: "${params.speak}"`)
    }

    // Resample from 22050 Hz (sam-js output) to AudioContext sample rate
    const src = buffer as Uint8Array
    const sourceRate = 22050
    const targetRate = this.audioContext.sampleRate
    const outLength = Math.max(1, Math.round((src.length * targetRate) / sourceRate))
    const out = new Float32Array(outLength)

    const ratio = sourceRate / targetRate
    for (let i = 0; i < outLength; i++) {
      const pos = i * ratio
      const i0 = Math.floor(pos)
      const i1 = Math.min(src.length - 1, i0 + 1)
      const t = pos - i0
      const s0 = (src[i0] - 128) / 128
      const s1 = (src[i1] - 128) / 128
      out[i] = s0 + (s1 - s0) * t
    }

    const audioBuffer = this.audioContext.createBuffer(1, out.length, targetRate)
    audioBuffer.getChannelData(0).set(out)

    const samData: SamData = {
      id,
      key,
      audioBuffer,
    }

    this.loadedSamples.set(id, samData)
    return samData
  }

  clear(): void {
    this.loadedSamples.clear()
  }
}
